// EditRecipe.tsx
import { Box, TextField, Divider, Stack, Typography, IconButton, Autocomplete, Chip, Dialog, Select, MenuItem } from "@mui/material";
import { AddBox } from "@mui/icons-material";
import CustomButton from "./default/CustomButton";
import ProcedureInput from "./ProcedureInput";
import { useEffect, useState, useCallback, useMemo } from "react";
import type { IEditData } from "../store/slices/editData";
import axiosService from "../utils/axios";
import { categoryImageMap } from "../utils/helpers/categoryImageMap";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import type { ITags } from "../types/tags";
import type { IRecipeIngredient, IRecipeTag } from "../types/recipe";
import { fetchTags } from "../utils/helpers/tagHelper";

import {
    setField,
    addIngredient,
    removeIngredient,
    addOldTag,
    addNewTag,
    removeOldTag,
    removeNewTag,
    setProcedure,
    addProcedure,
    removeProcedure,
    updateIngredientAmount,
    syncProcedure,
} from "../store/slices/editData";
import { getIngredientAmountMap } from "../utils/helpers/recipeHelper";
import { capitalizeFirstLetter } from "../utils/helpers/capitalizeFirstLetter";
import React from "react";

// *** Component Specific Types *** //
interface IEditRecipeProps {
    editData: IEditData;
    submit: () => void;
}

interface IIngredients {
    _id: string | null;
    name: string;
    category: string;
}

// *** Component Constants *** //
const DEBOUNCE_MS = 250;

// ***** MAIN COMPONENT ***** //
const EditRecipe: React.FC<IEditRecipeProps> = ({ editData, submit }) => {
    // *** Hooks *** //
    const dispatch = useDispatch();

    // *** Root States *** //
    const themeMode = useSelector((state: RootState) => state.theme.mode);
    const theme = useMemo(() => Theme(themeMode), [themeMode]);

    // *** Component States *** //
    const [nameLocal, setNameLocal] = useState(editData.name);
    const [descriptionLocal, setDescriptionLocal] = useState(editData.description);
    const [procedureLocal, setProcedureLocal] = useState<string[]>(editData.procedure);
    const [ingredientLocal, setIngredientLocal] = useState<IRecipeIngredient[]>(editData.ingredients);
    const [ingredientOptions, setIngredientOptions] = useState<IIngredients[]>([]);
    const [tags, setTags] = useState<ITags[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [currentIngredient, setCurrentIngredient] = useState<number | null>(null);
    const [ingredientSelectIndex, setIngredientSelectIndex] = useState(0);
    const [ingredientSelectModal, setIngredientSelectModal] = useState(false);
    const [ingredientAmountModal, setIngredientAmountModal] = useState(false);

    // *** Use Effects *** //
    useEffect(() => setNameLocal(editData.name), [editData.name]);

    useEffect(() => setDescriptionLocal(editData.description), [editData.description]);

    useEffect(() => setProcedureLocal(editData.procedure), [editData.procedure]);

    useEffect(() => setIngredientLocal(editData.ingredients), [editData.ingredients]);

    useEffect(() => {
        const id = setTimeout(() => {
            if (nameLocal !== editData.name) {
                dispatch(setField({ field: "name", value: nameLocal }));
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [nameLocal, editData.name, dispatch]);

    useEffect(() => {
        const id = setTimeout(() => {
            if (descriptionLocal !== editData.description) {
                dispatch(setField({ field: "description", value: descriptionLocal }));
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [descriptionLocal, editData.description, dispatch]);

    useEffect(() => {
        axiosService
            .get(`${process.env.REACT_APP_API_URL}/ingredients`)
            .then((res) => {
                const options: IIngredients[] = res.data.body.map((ingredient: IIngredients) => ({
                    _id: ingredient._id,
                    name: ingredient.name,
                    category: ingredient.category,
                }));
                setIngredientOptions(options);
            })
            .catch((err: Error) => console.error("Error sending data:", err));

        fetchTags()
            .then((res) => {
                setTags(res.data.body);
                setTagOptions(res.data.body.map((tag: ITags) => tag.name));
            })
            .catch((err: Error) => console.error("Error sending data:", err));
    }, [editData]);

    // Debounced sync -> Redux for procedure edits //
    useEffect(() => {
        const id = setTimeout(() => {
            const max = Math.max(procedureLocal.length, editData.procedure.length);
            for (let i = 0; i < max; i++) {
                if (procedureLocal[i] !== editData.procedure[i]) {
                    dispatch(setProcedure({ index: i, value: procedureLocal[i] ?? "" }));
                }
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [procedureLocal, editData.procedure, dispatch]);

    // *** Component Helpers *** //
    // Also commit on blur //
    const commitName = useCallback(() => {
        if (nameLocal !== editData.name) dispatch(setField({ field: "name", value: nameLocal }));
    }, [nameLocal, editData.name, dispatch]);

    const commitDescription = useCallback(() => {
        if (descriptionLocal !== editData.description) dispatch(setField({ field: "description", value: descriptionLocal }));
    }, [descriptionLocal, editData.description, dispatch]);

    // Commit a single step on blur //
    const commitProcedureIndex = useCallback(
        (index: number) => {
            const val = procedureLocal[index] ?? "";
            if (val !== editData.procedure[index]) {
                dispatch(setProcedure({ index, value: val }));
            }
        },
        [procedureLocal, editData.procedure, dispatch]
    );

    const handleAddIngredient = useCallback(
        (value: IIngredients) => {
            const payload: IRecipeIngredient = {
                name: value.name,
                ingredient: { _id: value._id, category: value.category } as any,
                amount: 0,
            };

            setIngredientLocal((prev) => [...prev, payload]);
            dispatch(addIngredient(payload));
        },
        [dispatch]
    );

    const closeAllModals = useCallback(() => {
        setIngredientSelectIndex(0);
        setIngredientAmountModal(false);
        setIngredientSelectModal(false);
    }, []);

    const handleAddOldTag = useCallback((value: IRecipeTag) => dispatch(addOldTag(value)), [dispatch]);
    const handleAddNewTag = useCallback((value: string) => dispatch(addNewTag(value)), [dispatch]);
    const handleRemoveOldTag = useCallback((value: string) => dispatch(removeOldTag(value)), [dispatch]);
    const handleRemoveNewTag = useCallback((value: string) => dispatch(removeNewTag(value)), [dispatch]);

    const handleRemoveTag = useCallback(
        (option: string) => {
            if (tagOptions.includes(option)) handleRemoveOldTag(option);
            else handleRemoveNewTag(option);
        },
        [handleRemoveOldTag, handleRemoveNewTag, tagOptions]
    );

    const handleRemoveIngredient = useCallback(
        (index: number) => {
            setIngredientLocal((prev) => prev.filter((_, i) => i !== index));

            dispatch(removeIngredient(index));
        },
        [dispatch]
    );

    const handleAddIngredientClick = useCallback(() => {
        closeAllModals();
        setIngredientSelectModal(true);
    }, [closeAllModals]);

    const handleIngredientChipClick = useCallback(
        (index: number) => {
            closeAllModals();
            setCurrentIngredient(index);
            setIngredientAmountModal(true);
        },
        [closeAllModals]
    );

    const handleProcedureChangeLocal = useCallback((index: number, value: string) => {
        setProcedureLocal((prev) => {
            const next = prev.slice();
            next[index] = value;
            return next;
        });
    }, []);

    const handleIngredientAmountChangeLocal = useCallback(
        (value: string) => {
            if (currentIngredient === null) return;

            setIngredientLocal((prev) => {
                const next = prev.slice();
                next[currentIngredient] = {
                    ...next[currentIngredient],
                    amount: Number(value),
                };

                return next;
            });
        },
        [currentIngredient]
    );

    const handleIngredientAmountChange = useCallback(() => {
        if (currentIngredient === null) return;

        dispatch(
            updateIngredientAmount({
                index: currentIngredient,
                value: ingredientLocal[currentIngredient].amount,
            })
        );

        closeAllModals();
    }, [closeAllModals, currentIngredient, dispatch, ingredientLocal]);

    const handleAddProcedure = useCallback(() => {
        setProcedureLocal((prev) => [...prev, ""]);

        dispatch(addProcedure());
    }, [dispatch]);

    const handleRemoveProcedure = useCallback(
        (index: number) => {
            setProcedureLocal((prev) => prev.filter((_, i) => i !== index));
            dispatch(removeProcedure(index));
        },
        [dispatch]
    );

    const handleProcedureOrderChange = useCallback(
        (index: number, type: "up" | "down") => {
            if (type !== "up" && type !== "down") return;

            let newProcedure: string[] = [];

            if (type === "up") {
                if (index === 0) {
                    return;
                }

                newProcedure = [
                    ...procedureLocal.slice(0, index - 1),
                    procedureLocal[index],
                    procedureLocal[index - 1],
                    ...procedureLocal.slice(index + 1),
                ];
            }

            if (type === "down") {
                if (index >= procedureLocal.length - 1) {
                    return;
                }

                newProcedure = [
                    ...procedureLocal.slice(0, index),
                    procedureLocal[index + 1],
                    procedureLocal[index],
                    ...procedureLocal.slice(index + 2),
                ];
            }

            dispatch(syncProcedure(newProcedure));
            setProcedureLocal(newProcedure);
        },
        [dispatch, procedureLocal]
    );

    const renderProcedure = useMemo(
        () =>
            procedureLocal.map((step: string, index: number) => (
                <ProcedureInput
                    key={index}
                    index={index}
                    value={step}
                    onChange={(index: number, value: string) => handleProcedureChangeLocal(index, value)}
                    onRemove={handleRemoveProcedure}
                    onBlur={commitProcedureIndex}
                    onUpdateOrder={handleProcedureOrderChange}
                />
            )),
        [procedureLocal, handleRemoveProcedure, commitProcedureIndex, handleProcedureOrderChange, handleProcedureChangeLocal]
    );

    const renderIngredients = useMemo(
        () => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                {ingredientLocal.map((item: IRecipeIngredient, index: number) => (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid",
                            borderColor: "#E5E7EB",
                            backgroundColor: theme.palette.background.default,
                            py: 1,
                            pr: 1.5,
                            pl: 1,
                            borderRadius: "8px",
                            minWidth: "180px",
                            height: "45px",
                            flexShrink: 0,
                            cursor: "pointer",
                        }}
                        onClick={() => handleIngredientChipClick(index)}
                    >
                        <Box
                            component="img"
                            src={item.ingredient ? categoryImageMap[item.ingredient.category] : categoryImageMap.alternative}
                            sx={{ height: "32px", width: "32px", mr: 1 }}
                        />
                        <Box sx={{ width: "100%" }}>
                            <Typography>{capitalizeFirstLetter(item.name)}</Typography>

                            {item.amount > 0 && (
                                <Box>
                                    <Divider />
                                    <Typography>{item.amount} grams</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        ),
        [handleIngredientChipClick, ingredientLocal, theme.palette.background.default]
    );

    const renderIngredientSelectModal = useMemo(
        () => (
            <Stack spacing={1} sx={{ borderRadius: 5, p: 1 }}>
                <Typography>Please choose the recipe's ingredients.</Typography>
                <Divider />
                <Autocomplete
                    disableClearable
                    multiple
                    options={ingredientOptions}
                    value={editData.ingredients.map((ingredient: IRecipeIngredient) => ({
                        name: ingredient.name,
                        _id: ingredient.ingredient ? ingredient.ingredient._id : null,
                        category: ingredient.ingredient ? ingredient.ingredient.category : "alternative",
                    }))}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option.name === value.name && option.category === value.category}
                    onChange={(_, newValue) => {
                        const added = newValue.find(
                            (item) => !editData.ingredients.some((ing: IRecipeIngredient) => ing.name === item.name)
                        );
                        if (added) handleAddIngredient(added);
                    }}
                    renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                            <li key={key} {...rest}>
                                {option.name}
                                <Typography component="span" sx={{ ml: 1, opacity: 0.6, fontSize: 12 }}>
                                    {option.category}
                                </Typography>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    label={option.name}
                                    {...chipProps}
                                    sx={{ backgroundColor: "#F97316", color: "white", fontWeight: 500, height: 24 }}
                                    onClick={() => handleRemoveIngredient(index)}
                                    onDelete={() => handleRemoveIngredient(index)}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="filled"
                            label="Ingredients"
                            placeholder="Add Ingredients"
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: 1,
                                "& .MuiFilledInput-root": {
                                    pt: 3,
                                    borderRadius: 1,
                                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
                                    "&:hover": { boxShadow: "inset 0 0 0 2px #F97316" },
                                    "&.Mui-focused": { boxShadow: "inset 0 0 0 2px #F97316", backgroundColor: "#fff" },
                                },
                            }}
                        />
                    )}
                />
                <CustomButton onClick={() => closeAllModals()} label="Continue" sx={{ color: "white" }} />
            </Stack>
        ),
        [closeAllModals, editData.ingredients, handleAddIngredient, handleRemoveIngredient, ingredientOptions]
    );

    const ingredientAmountMap: { value: string; label: string }[] = getIngredientAmountMap();

    const renderIngredientAmountModal = useMemo(() => {
        if (currentIngredient === null) return <Box>Test</Box>;
        else
            return (
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <TextField
                            variant="standard"
                            label="Amount"
                            value={ingredientLocal[currentIngredient].amount}
                            onChange={(e) => handleIngredientAmountChangeLocal(e.target.value)}
                        />
                        <Select value={ingredientAmountMap[ingredientSelectIndex].value} variant="outlined">
                            {ingredientAmountMap.map((item: { value: string; label: string }, index) => (
                                <MenuItem onClick={() => setIngredientSelectIndex(index)} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <CustomButton onClick={handleIngredientAmountChange} label={"Continue"} sx={{ mt: 2, width: "100%", color: "white" }} />
                </Box>
            );
    }, [
        currentIngredient,
        handleIngredientAmountChange,
        handleIngredientAmountChangeLocal,
        ingredientAmountMap,
        ingredientLocal,
        ingredientSelectIndex,
    ]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minHeight: "100%" }}>
            {/* Form Section */}
            <Box sx={{ backgroundColor: (theme) => theme.palette.background.paper, borderRadius: 2, p: 1, mb: 1 }}>
                <Box sx={{ mt: 1 }}>
                    <TextField
                        name="name"
                        label="Recipe Name"
                        value={nameLocal}
                        onChange={(e) => setNameLocal(e.target.value)}
                        onBlur={commitName}
                        variant="filled"
                        fullWidth
                        sx={{
                            backgroundColor: "#fff",
                            borderRadius: 1,
                            "& .MuiFilledInput-root": {
                                borderRadius: 1,
                                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
                                "&:hover": { boxShadow: "inset 0 0 0 2px #F97316" },
                                "&.Mui-focused": { boxShadow: "inset 0 0 0 2px #F97316", backgroundColor: "#fff" },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ my: 2 }}>
                    <Autocomplete
                        disableClearable
                        multiple
                        freeSolo
                        options={tagOptions}
                        value={[...editData.tags.old.map((tag: IRecipeTag) => tag.tag.name), ...editData.tags.new]}
                        onChange={(_, newValue) => {
                            const addedTag = newValue.find(
                                (item) =>
                                    ![...editData.tags.old, ...editData.tags.new].some((tag: IRecipeTag | string) =>
                                        typeof tag === "string" ? tag === item : tag.tag.name === item
                                    )
                            );
                            if (!addedTag) return;

                            if (tagOptions.includes(addedTag)) {
                                const existingTag = tags.find((tag: ITags) => tag.name === addedTag);
                                if (!existingTag) return;
                                handleAddOldTag({ tag: existingTag });
                            } else {
                                handleAddNewTag(addedTag);
                            }
                        }}
                        getOptionLabel={(option: string) => option}
                        isOptionEqualToValue={(option, value) => option === value}
                        filterSelectedOptions
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="filled"
                                label="Tags"
                                placeholder="Add tags"
                                sx={{
                                    backgroundColor: "#fff",
                                    borderRadius: 1,
                                    "& .MuiFilledInput-root": {
                                        borderRadius: 1,
                                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
                                        "&:hover": { boxShadow: "inset 0 0 0 2px #F97316" },
                                        "&.Mui-focused": { boxShadow: "inset 0 0 0 2px #F97316", backgroundColor: "#fff" },
                                    },
                                }}
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...chipProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        label={option}
                                        {...chipProps}
                                        sx={{ backgroundColor: "#F97316", color: "white", fontWeight: 500, height: 24 }}
                                        onClick={() => handleRemoveTag(option)}
                                        onDelete={() => handleRemoveTag(option)}
                                    />
                                );
                            })
                        }
                        sx={{
                            width: "100%",
                            ".MuiAutocomplete-inputRoot": { alignItems: "center", minHeight: "40px" },
                            ".MuiChip-root": { mt: 0.5 },
                        }}
                    />
                </Box>

                <Box>
                    <TextField
                        name="description"
                        label="Recipe Description"
                        value={descriptionLocal}
                        onChange={(e) => setDescriptionLocal(e.target.value)}
                        onBlur={commitDescription}
                        variant="filled"
                        fullWidth
                        multiline
                        sx={{
                            backgroundColor: "#fff",
                            borderRadius: 1,
                            "& .MuiFilledInput-root": {
                                borderRadius: 1,
                                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)",
                                "&:hover": { boxShadow: "inset 0 0 0 2px #F97316" },
                                "&.Mui-focused": { boxShadow: "inset 0 0 0 2px #F97316", backgroundColor: "#fff" },
                            },
                        }}
                    />
                </Box>
            </Box>

            <Divider />

            {/* Ingredients and Steps */}
            <Box sx={{ flexGrow: 1, px: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "1em", sm: "1.5em" } }} variant="h5">
                        Ingredients
                    </Typography>
                    <IconButton size="medium" onClick={() => handleAddIngredientClick()}>
                        <AddBox fontSize="large" />
                    </IconButton>
                </Stack>
                {renderIngredients}
                <Box>
                    <Stack direction="row" alignItems="center">
                        <Typography variant="h5" sx={{ fontSize: { xs: "1em", sm: "1.5em" } }}>
                            Steps
                        </Typography>
                        <IconButton size="medium" onClick={handleAddProcedure}>
                            <AddBox fontSize="large" />
                        </IconButton>
                    </Stack>
                    <Stack spacing={1}>{renderProcedure}</Stack>
                </Box>
            </Box>

            {/* Submit button */}
            <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
                <CustomButton
                    onClick={submit}
                    label="Save Recipe"
                    sx={{
                        fontSize: { xs: "1em", sm: "1.5em" },
                        color: "white",
                        height: { xs: "32px", sm: "48px" },
                        width: { xs: "100%", sm: "60%", md: "40%" },
                    }}
                />
            </Box>

            {/* Ingredient Selection Modal */}
            <Dialog open={ingredientAmountModal || ingredientSelectModal} onClose={() => closeAllModals()}>
                {ingredientSelectModal && renderIngredientSelectModal}
                {ingredientAmountModal && renderIngredientAmountModal}
            </Dialog>
        </Box>
    );
};

export default React.memo(EditRecipe);
